import { defineStore, acceptHMRUpdate, skipHydrate } from 'pinia'

type UserIpInformation = {
  country_code: string
  country_name: string
  latitude: number
  longitude: number
}

type UserLocation = {
  type: 'pending' | 'gps' | 'ip' | 'query' | 'location'
  latitude: number
  longitude: number
}

export const useUserStore = defineStore('user', () => {
  const { isSupported, pause } = useGeolocation({ immediate: false })
  const permissionsSupported = useSupported(() => navigator && 'permissions' in navigator)
  const onboarded = useLocalStorage('onboarded', false)
  const geocoded = useGeocodedStore()
  const search = useSearchStore()

  const countryCode = ref<string>()
  const countryName = ref<string>()
  const useMyLocation = ref(false)
  const locale = ref<UserLocation>({
    type: 'pending',
    latitude: roundCoordinate(39.8283),
    longitude: roundCoordinate(-98.5795)
  })
  const permissionStatus = ref<PermissionState>('prompt')

  async function initialise() {

    const status = await checkGeolocationPermission()
    permissionStatus.value = status

    const { hasLocation } = await fetchLocationFromQuery()

    if (!hasLocation) {
        // fall back to using the user agent's location
        try {
          await fetchGeoLocation()
        } catch (err) {
          console.warn('fetchGeoLocation failed:', err)
        }
    }
  }

  async function checkGeolocationPermission(): Promise<PermissionState> {
    // this is for devices that don't support the permissions API
    if (typeof window === 'undefined' || !('permissions' in navigator)) return 'prompt'

    const geoLocationPermissionStatus = await navigator.permissions.query({ name: 'geolocation' })
    geoLocationPermissionStatus.onchange = () => {
      permissionStatus.value = geoLocationPermissionStatus.state
    }

    return geoLocationPermissionStatus.state
  }

  async function fetchLocationFromQuery() {
    if (useMyLocation.value) return { hasLocation: true }

    const { query } = useRoute()
    const { x: longitude, y: latitude, location: placeId } = query

    if (longitude && latitude) {
      locale.value = {
        type: 'query',
        latitude: roundCoordinate(Number(latitude)),
        longitude: roundCoordinate(Number(longitude))
      }
      return { hasLocation: true }
    }

    if (!placeId) return { hasLocation: false }

    await geocoded.fetchGeocodedPlace(placeId.toString())
    if (!geocoded.geocodedPlace) return { hasLocation: false }

    locale.value = {
      type: 'location',
      latitude: roundCoordinate(geocoded.geocodedPlace.latitude),
      longitude: roundCoordinate(geocoded.geocodedPlace.longitude)
    }
    search.location = geocoded.geocodedPlace

    return {
      hasLocation: true
    }
  }

  function fetchGeoLocation(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!isSupported.value) return reject(new Error('Geolocation not supported'))

        let status: PermissionState = 'prompt'
        if (typeof window !== 'undefined' && 'permissions' in navigator) {
          const result = await navigator.permissions.query({ name: 'geolocation' })
          status = result.state
          // even if permission is denied we still try to get location to trigger the browser prompt, so the user can change their mind.
        }

        navigator.geolocation.getCurrentPosition(
          position => {
            locale.value = {
              type: 'gps',
              latitude: roundCoordinate(position.coords.latitude),
              longitude: roundCoordinate(position.coords.longitude)
            }
            useMyLocation.value = true
            permissionStatus.value = 'granted'
            resolve()
          },
          error => {
            console.warn('getCurrentPosition error:', error)
            useMyLocation.value = false
            permissionStatus.value = 'denied'
            reject(error)
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 300_000 // 5 minutes
          }
        )
      } catch (e) {
        console.error('Unexpected fetchGeoLocation error:', e)
        reject(e)
      }
    })
  }

  function pauseGeoLocation() {
    pause()
    useMyLocation.value = false
  }

  return {
    // state
    countryCode,
    countryName,
    locale,
    isSupported,
    onboarded: skipHydrate(onboarded),
    permissionStatus,

    // getters
    coordinates: computed(() => [locale.value.longitude, locale.value.latitude]),
    useMyLocation,

    // actions
    fetchGeoLocation,
    pauseGeoLocation,
    fetchLocationFromQuery,
    initialise
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
